import { useMemo, useState } from 'preact/hooks';
import type { Chain, PaymentOption, PublicSession } from '../api/types';
import { CHAINS, ECOSYSTEMS, chainLabel, type Ecosystem } from '../chains';
import { t, type MessageKey } from '../i18n';
import { optKey, trimDecimal } from '../utils/format';

interface Props {
  session: PublicSession;
  selectedKey: string | null;
  onSelect(o: PaymentOption): void;
}

const LAST_NETWORK_KEY = 'cv-last-network';

export function rememberNetwork(chain: Chain): void {
  try {
    localStorage.setItem(LAST_NETWORK_KEY, chain);
  } catch {
    /* storage unavailable */
  }
}
export function lastNetwork(): Chain | null {
  try {
    return (localStorage.getItem(LAST_NETWORK_KEY) as Chain | null) ?? null;
  } catch {
    return null;
  }
}

/**
 * Two-step picker: network (grouped by ecosystem) then asset on that network.
 * The last-used network is remembered per browser so repeat payers land on it.
 */
export function AssetPicker({ session, selectedKey, onSelect }: Props) {
  const opts = session.payment_options;
  const chainsAvailable = useMemo(() => {
    const seen = new Set<Chain>();
    for (const o of opts) seen.add(o.chain);
    return [...seen].sort((a, b) => Object.keys(CHAINS).indexOf(a) - Object.keys(CHAINS).indexOf(b));
  }, [opts]);

  const initial = (() => {
    const sel = opts.find((o) => optKey(o) === selectedKey)?.chain;
    if (sel) return sel;
    const last = lastNetwork();
    if (last && chainsAvailable.includes(last)) return last;
    return chainsAvailable[0] ?? null;
  })();
  const [chain, setChain] = useState<Chain | null>(initial);

  const grouped = useMemo(() => {
    const g = new Map<Ecosystem, Chain[]>();
    for (const c of chainsAvailable) {
      const eco = CHAINS[c].ecosystem;
      if (!g.has(eco)) g.set(eco, []);
      g.get(eco)!.push(c);
    }
    return ECOSYSTEMS.filter((e) => g.has(e.id)).map((e) => ({ ...e, chains: g.get(e.id)! }));
  }, [chainsAvailable]);

  const assets = chain ? opts.filter((o) => o.chain === chain) : [];

  return (
    <section aria-labelledby="cv-picker-title" class="cv-picker">
      <h2 id="cv-picker-title" class="cv-section-title">
        {t('picker.title')}
      </h2>

      <div class="cv-field" style={{ marginBottom: 12 }}>
        <label for="cv-network">{t('picker.network')}</label>
        <select
          id="cv-network"
          class="cv-select mono"
          value={chain ?? ''}
          onChange={(e) => {
            const c = (e.currentTarget as HTMLSelectElement).value as Chain;
            setChain(c);
            rememberNetwork(c);
          }}
        >
          {grouped.map((g) => (
            <optgroup key={g.id} label={t(`picker.ecosystem.${g.id}` as MessageKey)}>
              {g.chains.map((c) => (
                <option key={c} value={c}>
                  {chainLabel(c)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div class="cv-network-pills" role="tablist" aria-label={t('picker.network')}>
        {chainsAvailable.map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={c === chain}
            class={`cv-pill${c === chain ? ' on' : ''}`}
            onClick={() => {
              setChain(c);
              rememberNetwork(c);
            }}
          >
            <span class="dot" style={{ background: CHAINS[c].color }} aria-hidden="true" />
            {CHAINS[c].label}
          </button>
        ))}
      </div>

      <h3 class="cv-section-title" style={{ marginTop: 14 }}>
        {t('picker.asset')}
      </h3>
      <ul class="cv-options" role="list">
        {assets.map((o) => {
          const key = optKey(o);
          return (
            <li key={key}>
              <button
                type="button"
                class="cv-option"
                aria-pressed={selectedKey === key}
                onClick={() => {
                  rememberNetwork(o.chain);
                  onSelect(o);
                }}
              >
                <span class="asset">
                  <span class="dot" style={{ background: CHAINS[o.chain].color }} aria-hidden="true" />
                  {o.symbol}
                </span>
                <span class="net">{o.token_contract ? t('picker.token') : t('picker.native')}</span>
                <span class="amt mono">
                  {trimDecimal(o.amount)} {o.symbol}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
